import { Button } from '@/components/ui/button';
import { HelpTypePreview } from '@/nodes/HelpTypePreview';
import {
    EquipmentTypeDisplay,
    NodeBody,
    NodeContainer,
    NodeErrorIcon,
    NodeHeader,
    NodeOptionRows,
    NodePrompts,
} from '@/nodes/NodeUI';
import { equipmentTypeState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { type EquipmentTypeId, type GuideNodeId, isSelectField, outgoingHandles } from '@rock/types';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { type FC, useContext } from 'react';
import { FlowParamsContext } from '../app/FlowInitializer';
import type { EquipmentAttributeNodeType } from './types';

const EquipmentAttributeNode: FC<NodeProps<EquipmentAttributeNodeType>> = ({ id, selected, data }) => {
    const { spaceId } = useContext(FlowParamsContext);
    const paths = outgoingHandles['equipment-attribute'](id as GuideNodeId, data);
    const type = useAsyncAtomValue(
        equipmentTypeState(data.equipmentTypeId === '[context]' ? ('_' as EquipmentTypeId) : data.equipmentTypeId),
    );
    const inputFields = type?.inputFields;
    const field = inputFields?.find((f) => f.dataKey === data.fieldDataKey);
    // Same predicate the app switches rendering on, so the canvas shows the screen the author
    // will actually get: options instead of a field, and no continue button.
    const isSelect = !!field && isSelectField(field);

    return (
        <NodeContainer>
            <NodeErrorIcon id={id} />
            <NodeHeader id={id} type={data.type} title={data.pageTitle} />
            <NodeBody selected={selected}>
                <NodePrompts prompts={data.prompts} />
                {data.equipmentTypeId && (
                    <div className="flex flex-col items-center justify-center p-1">
                        <EquipmentTypeDisplay spaceId={spaceId} typeId={data.equipmentTypeId} />
                    </div>
                )}
                <div className="flex flex-col items-stretch gap-1 px-5 my-3">
                    <div className="text-xs font-medium">Field</div>
                    <div className="bg-white text-xs text-left p-2">
                        {field?.label || data.fieldDataKey || '(none selected)'}
                    </div>
                </div>
                <div className="flex flex-col items-end px-3">
                    <HelpTypePreview
                        helpType={data.helpType}
                        spaceId={spaceId}
                        modelNumberHelpEnabled={field?.dataType === 'model-number'}
                    />
                </div>
                {isSelect ? (
                    <NodeOptionRows options={field.options ?? []} showNotSure={data.optional} />
                ) : (
                    <div className="flex flex-col items-center mb-2">
                        {data.optional && (
                            <Button size="sm" variant="link">
                                Skip
                            </Button>
                        )}
                        <Button size="sm">{data.buttonLabel || 'Button'}</Button>
                    </div>
                )}
            </NodeBody>

            <Handle type="target" position={Position.Left} />
            <Handle type="source" id={paths.next} position={Position.Right} />
        </NodeContainer>
    );
};

export default EquipmentAttributeNode;
